import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, Switch, TextField, Typography
} from '@mui/material';
import { DndContext, useDraggable, useDroppable, closestCenter } from '@dnd-kit/core';
import PromoChip from 'components/shared/PromoChip';
import { CREATE_COUPON, UPDATE_COUPON, COUPONS, SUBSCRIBABLE_PLANS } from 'api/graphql/coupon';

const todayIso = () => new Date().toISOString().slice(0, 10);

function PlanChip({ plan, zone }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `plan-${plan.id}`, data: { planId: plan.id, zone } });
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{ display: 'inline-flex', cursor: 'grab', opacity: isDragging ? 0.4 : 1, touchAction: 'none' }}
    >
      <Chip label={plan.name} sx={{ backgroundColor: 'surface.raised', border: '1px solid', borderColor: 'hairline.strong', fontWeight: 600 }} />
    </Box>
  );
}
PlanChip.propTypes = { plan: PropTypes.object.isRequired, zone: PropTypes.string.isRequired };

function DropZone({ id, title, children, accent = false }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{title}</Typography>
      <Box
        ref={setNodeRef}
        sx={{
          mt: 0.75, minHeight: 96, p: 1.5, borderRadius: '12px',
          border: '1px dashed', borderColor: isOver ? 'gold.main' : 'hairline.strong',
          backgroundColor: accent && isOver ? 'gold.soft' : 'surface.content',
          display: 'flex', flexWrap: 'wrap', gap: 1, alignContent: 'flex-start'
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
DropZone.propTypes = { id: PropTypes.string.isRequired, title: PropTypes.string.isRequired, children: PropTypes.node.isRequired, accent: PropTypes.bool };

/**
 * Formulario de creación/edición de cupón. Incluye la asociación de planes por drag-and-drop
 * (arrastra un plan a la "zona del cupón") con preview del PromoChip.
 */
function CouponForm({ open, coupon = null, onClose }) {
  const { t } = useTranslation(['coupons', 'buttons']);
  const isEdit = Boolean(coupon);

  const [code, setCode] = useState(coupon?.code ?? '');
  const [discountValue, setDiscountValue] = useState(coupon?.discount_value ?? 10);
  const [audience, setAudience] = useState(coupon?.audience ?? 'all');
  const [validFrom, setValidFrom] = useState(coupon?.valid_from?.slice(0, 10) ?? todayIso());
  const [validTo, setValidTo] = useState(coupon?.valid_to?.slice(0, 10) ?? todayIso());
  const [maxUses, setMaxUses] = useState(coupon?.max_uses ?? '');
  const [isActive, setIsActive] = useState(coupon?.is_active ?? true);
  const [assignedIds, setAssignedIds] = useState((coupon?.plans ?? []).map((p) => Number(p.id)));
  const [error, setError] = useState('');

  const { data: plansData } = useQuery(SUBSCRIBABLE_PLANS, { fetchPolicy: 'cache-and-network' });
  const allPlans = useMemo(() => (plansData?.plans?.data ?? []).map((p) => ({ ...p, id: Number(p.id) })), [plansData]);
  const assigned = allPlans.filter((p) => assignedIds.includes(p.id));
  const available = allPlans.filter((p) => !assignedIds.includes(p.id));

  const refetchQueries = [{ query: COUPONS, variables: { first: 15, page: 1, search: '', status: '' } }];
  const [createCoupon, { loading: creating }] = useMutation(CREATE_COUPON, { refetchQueries });
  const [updateCoupon, { loading: updating }] = useMutation(UPDATE_COUPON, { refetchQueries });
  const saving = creating || updating;

  const handleDragEnd = ({ over, active }) => {
    if (!over) return;
    const planId = active.data.current?.planId;
    if (planId == null) return;
    if (over.id === 'assigned') setAssignedIds((prev) => (prev.includes(planId) ? prev : [...prev, planId]));
    if (over.id === 'available') setAssignedIds((prev) => prev.filter((id) => id !== planId));
  };

  const handleSubmit = async () => {
    setError('');
    const input = {
      code: code.trim(),
      discount_type: 'percentage',
      discount_value: Number(discountValue),
      audience,
      valid_from: validFrom,
      valid_to: validTo,
      max_uses: maxUses === '' ? null : Number(maxUses),
      is_active: isActive,
      plan_ids: assignedIds.map(String)
    };
    try {
      if (isEdit) await updateCoupon({ variables: { id: coupon.id, input } });
      else await createCoupon({ variables: { input } });
      onClose(true);
    } catch (e) {
      setError(e.message || t('saveError', { ns: 'coupons', defaultValue: 'No se pudo guardar el cupón.' }));
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md" PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none' } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {isEdit ? t('editCoupon', { ns: 'coupons', defaultValue: 'Editar cupón' }) : t('newCoupon', { ns: 'coupons', defaultValue: 'Nuevo cupón' })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('code', { ns: 'coupons', defaultValue: 'Código' })} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} fullWidth size="small" />
            <TextField label={t('discountPct', { ns: 'coupons', defaultValue: 'Descuento %' })} type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} fullWidth size="small" inputProps={{ min: 1, max: 100 }} />
            <TextField select label={t('audience', { ns: 'coupons', defaultValue: 'Aplica a' })} value={audience} onChange={(e) => setAudience(e.target.value)} fullWidth size="small">
              <MenuItem value="all">{t('audienceAll', { ns: 'coupons', defaultValue: 'Todos los clientes' })}</MenuItem>
              <MenuItem value="national">{t('audienceNational', { ns: 'coupons', defaultValue: 'Nacionales (Colombia)' })}</MenuItem>
              <MenuItem value="international">{t('audienceInternational', { ns: 'coupons', defaultValue: 'Internacionales' })}</MenuItem>
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('validFrom', { ns: 'coupons', defaultValue: 'Válido desde' })} type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            <TextField label={t('validTo', { ns: 'coupons', defaultValue: 'Válido hasta' })} type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            <TextField label={t('maxUses', { ns: 'coupons', defaultValue: 'Usos máx.' })} type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} fullWidth size="small" placeholder="∞" inputProps={{ min: 1 }} />
          </Stack>
          <FormControlLabel control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />} label={t('active', { ns: 'coupons', defaultValue: 'Activo' })} />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('assignPlans', { ns: 'coupons', defaultValue: 'Planes asociados' })}</Typography>
              <PromoChip discountValue={discountValue || 0} />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('dragHint', { ns: 'coupons', defaultValue: 'Arrastra un plan a la zona del cupón. Sin planes = aplica a todos.' })}
            </Typography>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
                <DropZone id="available" title={t('availablePlans', { ns: 'coupons', defaultValue: 'Planes disponibles' })}>
                  {available.length === 0
                    ? <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
                    : available.map((p) => <PlanChip key={p.id} plan={p} zone="available" />)}
                </DropZone>
                <DropZone id="assigned" title={t('couponZone', { ns: 'coupons', defaultValue: 'Zona del cupón' })} accent>
                  {assigned.length === 0
                    ? <Typography variant="caption" sx={{ color: 'text.disabled' }}>{t('appliesAll', { ns: 'coupons', defaultValue: 'Aplica a todos los planes' })}</Typography>
                    : assigned.map((p) => <PlanChip key={p.id} plan={p} zone="assigned" />)}
                </DropZone>
              </Stack>
            </DndContext>
          </Box>

          {error && <Typography variant="body2" sx={{ color: 'error.main' }}>{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button color="inherit" onClick={() => onClose(false)} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>
          {t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={saving || !code.trim()} sx={{ textTransform: 'none', fontWeight: 700 }} startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          {t('save', { ns: 'buttons', defaultValue: 'Guardar' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CouponForm.propTypes = {
  open: PropTypes.bool.isRequired,
  coupon: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default CouponForm;
